import { useCommunityTopics } from '@communities/content-management/hooks';
import { Select } from '@soteria/design-system-ve';
import { useCallback, useEffect, useMemo } from 'react';

interface TopicSelectorProps {
  communityId?: string;
  selectedTopic: string | null;
  onTopicChange: (topicName: string | null) => void;
}

/**
 * TopicSelector - allows selection of a topic from available community topics
 * Defaults to the first topic in the list when topics are loaded
 */
export function TopicSelector({ communityId, selectedTopic, onTopicChange }: TopicSelectorProps) {
  const { topics, loading } = useCommunityTopics(communityId);

  const options = useMemo(() => {
    if (topics.length === 0) {
      return [{ label: 'No Topics Available', value: '' }];
    }

    return topics.map(topic => ({
      label: topic.name,
      value: topic.name,
    }));
  }, [topics]);

  // Auto-select first topic when topics are loaded and no topic is selected
  useEffect(() => {
    if (!loading && topics.length > 0 && !selectedTopic) {
      onTopicChange(topics[0].name);
    }
  }, [loading, topics, selectedTopic, onTopicChange]);

  const handleChange = useCallback(
    (value: string) => {
      onTopicChange(value || null);
    },
    [onTopicChange]
  );

  if (!communityId) {
    return null;
  }

  const hasNoTopics = !loading && topics.length === 0;

  return <Select disabled={loading || hasNoTopics} label="Topic" options={options} value={selectedTopic || ''} onChange={handleChange} />;
}
